<script setup lang="ts">
import { KsSpinner } from '@aschehoug/kloss'
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons'
import { vElementVisibility } from '@vueuse/components'
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
  Label,
} from 'reka-ui'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    id?: string
    placeholder?: string
    name?: string
    label?: string
    items?: BrregEnhet[]
  }>(),
  {
    id: 'organization',
    placeholder: 'Din bedrift...',
    name: undefined,
    label: 'Søk din bedrift i Brønnøysundregistrene',
  },
)

const input = defineModel<string>('search')

const debounced = refDebounced(input, 500)

const error = defineModel<string | boolean>('error', { default: false })

const selectedOrganization = defineModel<BrregEnhet>({
  default: undefined,
})

const { data, fetchStatus, fetchNextPage } = useBrregQuery(debounced, selectedOrganization)

const items = computed(() => data.value?.pages?.flatMap(it => it?._embedded?.enheter ?? []) ?? props.items ?? [])

function createVisibilityHandler(index: number) {
  return (isVisible: boolean) => {
    if (isVisible && index + 1 === items.value.length) {
      fetchNextPage()
    }
  }
}
</script>

<template>
  <ComboboxRoot
    v-slot="{ open }"
    v-model="selectedOrganization"
    class="relative"
    ignore-filter
  >
    <Label :for="id">{{ label }}</Label>
    <ComboboxAnchor>
      <ComboboxInput :id="id" v-model="input" autocomplete="off" :display-value="item => item?.navn" :name="name || id" class="mt-2xs" :class="[inputVariants.base({ error: !!error }), $attrs.class]" :placeholder="placeholder" @focus="$event.target.select()" />
      <ComboboxTrigger class="relative">
        <KsSpinner v-if="fetchStatus === 'fetching'" size="small" class="absolute right-m -top-5 [--ks-primary:theme(colors.blurple.300)]" />
        <Icon v-else class="absolute right-m -top-4" :icon="faMagnifyingGlass" />
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxContent
      class="z-10 absolute overflow-hidden bg-white mt-2 rounded-lg space-y-2.5xs pl-s pr-m border-2 transition-[max-height,border-color] duration-300 ease-in-out"
      :class="open && items.length ? 'border-blurple-300 max-h-80' : 'border-transparent max-h-0'"
    >
      <ComboboxViewport>
        <!-- TODO: No results placeholder -->
        <ComboboxItem
          v-for="(item, i) in items"
          :key="item.organisasjonsnummer"
          v-element-visibility="createVisibilityHandler(i)"
          :value="item"
          :text-value="[item.navn, item.organisasjonsnummer].join(' ')"
          class="py-xs px-s text-blurple-500 border-b border-b-carbon-100 data-[highlighted]:bg-blurple-50"
        >
          {{ item.navn }}
        </ComboboxItem>
      </ComboboxViewport>
    </ComboboxContent>
  </ComboboxRoot>
</template>
